import React, { useMemo } from 'react';
import {
    Breadcrumbs as MuiBreadcrumbs,
    Link,
    Box,
    LinkProps,
} from '@mui/material';
import { SVGS } from '../../assets/images';
import {
    Link as RouterLink,
    useLocation
} from 'react-router-dom';
import styles from './breadcrumbs.module.scss';
import { FormattedMessage } from 'react-intl';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import { useAppSelector } from '../../hooks/hooks';
import { matchRoute } from './matchRoute';
import { Lx } from '../../i18n/consts';
import { useBreadcrumbOverride } from './override-context';

export const STATIC_ROUTE_LABELS: Record<string, React.ReactNode> = {
    [NAVIGATION_ROUTES.MAIN]: '',
    [NAVIGATION_ROUTES.TYPES]: <FormattedMessage id={'types'}/>,
    [NAVIGATION_ROUTES.PROJECT_TYPES]: <FormattedMessage id={'project_types'}/>,
    [NAVIGATION_ROUTES.SHIFT_TYPES]: <FormattedMessage id={'shift_types'}/>,
    [NAVIGATION_ROUTES.ABSENCE_TYPES]: <FormattedMessage id={'absences_types'}/>,
    [NAVIGATION_ROUTES.TAG_TYPES]: <FormattedMessage id={'tags_types'}/>,
    [NAVIGATION_ROUTES.ROLES]: <FormattedMessage id={'roles'}/>,
    [NAVIGATION_ROUTES.CLIENTS]: <FormattedMessage id={'clients'}/>,
    [NAVIGATION_ROUTES.EMPLOYEES]: <FormattedMessage id={'employees'}/>,
    [NAVIGATION_ROUTES.PROJECTS]: <FormattedMessage id={'projects'}/>,
    [NAVIGATION_ROUTES.TIMESHEET]: <FormattedMessage id={'timesheet'}/>,
    [NAVIGATION_ROUTES.CREATE_SHIFT]: <FormattedMessage id={Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT} />,
    [NAVIGATION_ROUTES.VACATIONREPORT]: <FormattedMessage id={'vacation_report'}/>,
    [NAVIGATION_ROUTES.CREATE_VACATION]: <FormattedMessage id={Lx.Vacation.CREATE_NEW_VACATION} />,
    [NAVIGATION_ROUTES.SETTINGS]: <FormattedMessage id={'settings'}/>,
    [NAVIGATION_ROUTES.SETTINGS_CALENDAR]: <FormattedMessage id={'work_calendar'}/>,
    [NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS]: <FormattedMessage id={'integrations'}/>,
    [NAVIGATION_ROUTES.COSTREPORT]: <FormattedMessage id={'costreport'}/>,
    [NAVIGATION_ROUTES.TIMEREPORT]: <FormattedMessage id={'timereport'}/>,
    [NAVIGATION_ROUTES.TIMEBANKREPORT]: <FormattedMessage id={'timebankreport'}/>,
    [NAVIGATION_ROUTES.CREATE_TIME_BANK_TRANSACTION]: <FormattedMessage id={'timebankreport_manual_transaction'}/>,
};

const SEGMENT_LABELS: Record<string, React.ReactNode> = {
    'manual-transaction': 'Manual transaction',
};

interface LinkRouterProps extends LinkProps {
    to: string;
    replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
    return <Link {...props} component={RouterLink as any} />;
}

const Breadcrumbs: React.FC = () => {
    const projectTypes = useAppSelector(state => state.projects.types);
    const { override } = useBreadcrumbOverride();

    const location = useLocation();

    const DYNAMIC_ROUTE_LABELS: Record<string, (params: Record<string, string>) => React.ReactNode> = {
        // projects
        [NAVIGATION_ROUTES.PROJECTS_BY_TYPE]: (params) => {
            const typeIdAsNumber = Number(params.typeId);
            
            if (isNaN(typeIdAsNumber)) { // is not valid
                return params.typeId;
            }

            const projectName = projectTypes.find(x => x.id === typeIdAsNumber)?.name;
            return projectName || params.typeId;
        },
        [NAVIGATION_ROUTES.EDIT_SHIFT]: () => <FormattedMessage id={Lx.Schedule.EDIT_TIMESHIFT_SHIFT} />,
        [NAVIGATION_ROUTES.EDIT_ABSENCE]: () => <FormattedMessage id={Lx.Schedule.EDIT_TIMESHIFT_ABSENCE} />,
    };


    const pathnames = location.pathname.split('/').filter((x) => x);

    const resolveLabelForPath = React.useCallback((path: string): React.ReactNode => {
        const staticLabel = STATIC_ROUTE_LABELS[path];
        if (staticLabel) {
            return staticLabel;
        }

        for (const [routePattern, dynamicLabel] of Object.entries(DYNAMIC_ROUTE_LABELS)) {
            const match = matchRoute(path, routePattern);
            if (match) {
                return dynamicLabel(match.params);
            }
        }

        const segments = path.split('/').filter(Boolean);
        const lastSegment = segments[segments.length - 1];
        return lastSegment ? SEGMENT_LABELS[lastSegment] ?? lastSegment : path;
    }, [DYNAMIC_ROUTE_LABELS]);

    const breadcrumbItems = useMemo(() => {
        return pathnames.map((value, index) => {
            const to = `/${pathnames.slice(0, index + 1).join('/')}`;
            const last = index === pathnames.length - 1;

            const label = resolveLabelForPath(to) ?? value;

            return { to, label, last };
        });
    }, [pathnames, resolveLabelForPath]);

    if (override && location.pathname === override.to) {
        const label = override.label ?? resolveLabelForPath(override.from);

        return (
            <div className={styles.breadcrumbsBox}>
                <MuiBreadcrumbs
                    aria-label="breadcrumb"
                    className={styles.breadcrumbs}
                    separator={
                        <Box
                            component="span"
                            className={styles.separator}>
                            <SVGS.ArrowKeyboard className={styles.arrow} />
                        </Box>
                    }>
                    <LinkRouter 
                        className={styles.link} 
                        underline="hover" 
                        color="inherit" 
                        to={override.from}>
                        {label}
                    </LinkRouter>
                    <Box component="span" className={styles.link} aria-hidden="true">
                        {'\u00a0'}
                    </Box>
                </MuiBreadcrumbs>
            </div>
        );
    }

    return (
        <div className={styles.breadcrumbsBox}>
            <MuiBreadcrumbs
                aria-label="breadcrumb"
                className={styles.breadcrumbs}
                separator={
                    <Box
                        component="span"
                        className={styles.separator}>
                        <SVGS.ArrowKeyboard className={styles.arrow} />
                    </Box>
                }>
                {breadcrumbItems.map(({ to, label, last }) => {
                    return last 
                        ? (
                            <span className={styles.link} aria-hidden="true" key={`${to}-current`}>
                            </span>
                        )
                        : (
                            <LinkRouter 
                                className={styles.link} 
                                underline="hover" 
                                color="inherit" 
                                to={to} 
                                key={to}>
                                {label}
                            </LinkRouter>
                        );
                })}
            </MuiBreadcrumbs>
        </div>
    );
};

export default Breadcrumbs;
